import { useState } from "react";
import { Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";

export interface EntityOption {
  value: string;
  label: string;
}

// A searchable Select backed by an async fetcher (debounced). Used to pick a
// user/driver by name/phone for support-ticket creation.
export function EntitySearchSelect({
  label,
  placeholder,
  value,
  onChange,
  queryKey,
  fetcher,
  required,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (id: string) => void;
  queryKey: string;
  fetcher: (search: string) => Promise<EntityOption[]>;
  required?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);

  const q = useQuery({
    queryKey: ["entity-search", queryKey, debounced],
    queryFn: () => fetcher(debounced),
  });

  return (
    <Select
      label={label}
      placeholder={placeholder ?? "Search by name or phone…"}
      data={q.data ?? []}
      value={value || null}
      onChange={(v) => onChange(v ?? "")}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      nothingFoundMessage={q.isFetching ? "Searching…" : "No matches"}
      clearable
      required={required}
    />
  );
}
